import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  CheckCircle2,
  Circle,
  LogOut,
  Plane,
} from 'lucide-react';
import DashboardLayout from '../components/Layout/DashboardLayout';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import { StatusBadge } from '../components/UI/StatusBadge';
import ConfirmModal from '../components/UI/ConfirmModal';
import {
  useCompleteOffboardingTask,
  useMyOffboardingCase,
} from '../hooks/useOffboardingCases';
import { formatDate } from '../utils/dateUtils';
import { formatLeaveDays } from '../utils/numberUtils';

const SelfServiceOffboarding = () => {
  const navigate = useNavigate();
  const [confirmFlight, setConfirmFlight] = useState(false);
  const { data: offboardingCase, isLoading, isError, refetch } = useMyOffboardingCase();
  const completeTask = useCompleteOffboardingTask();

  const employeeTasks = (offboardingCase?.tasks || []).filter(
    (task) => task.actor === 'Employee' && task.task_key !== 'employee_acknowledgement',
  );
  const flightArrangementsReady = Boolean(
    offboardingCase?.departure_date && offboardingCase?.has_flight_ticket,
  );

  const completeFlightReadiness = () => {
    completeTask.mutate(
      {
        id: offboardingCase.id,
        taskKey: 'flight_readiness_acknowledgement',
        data: { payload: { confirmed: true } },
      },
      { onSettled: () => setConfirmFlight(false) },
    );
  };

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto px-4 py-6">
        <div className="flex items-center gap-3 mb-6">
          <button
            type="button"
            className="icon-button"
            onClick={() => navigate('/self-service')}
            aria-label="Back to self-service"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">My Offboarding</h1>
            <p className="text-sm text-gray-500">Complete the tasks HR has released to you.</p>
          </div>
        </div>

        {isLoading && <div className="flex justify-center py-12"><LoadingSpinner /></div>}

        {isError && (
          <div className="text-center py-12">
            <LogOut className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Could not load your plan</h3>
            <p className="text-sm text-gray-500">Try again in a moment.</p>
            <button type="button" className="btn btn-primary mt-4" onClick={() => refetch()}>
              Retry
            </button>
          </div>
        )}

        {!isLoading && !isError && !offboardingCase && (
          <div className="bg-white rounded-2xl border shadow-sm">
            <div className="text-center py-12 px-6">
              <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <LogOut className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No offboarding plan yet</h3>
              <p className="text-sm text-gray-500 max-w-sm mx-auto">
                HR will release your exit plan here when it is ready. Ordinary annual leave still goes through Leave Applications.
              </p>
            </div>
          </div>
        )}

        {offboardingCase && (
          <div className="space-y-4">
            <div className="bg-white rounded-2xl border shadow-sm p-5">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm text-gray-500">Employment end date</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {offboardingCase.revised_employment_end_date
                      ? `${formatDate(offboardingCase.revised_employment_end_date)} · after ${offboardingCase.revised_employment_end_session === 'AM' ? 'morning' : 'afternoon'}`
                      : 'To be confirmed'}
                  </p>
                </div>
                <StatusBadge status={offboardingCase.workflow_status} />
              </div>
              {offboardingCase.leave_settlement?.confirmed && (
                <p className="text-sm text-gray-600 mt-3">
                  Annual leave:{' '}
                  {offboardingCase.leave_settlement.preference === 'encashment'
                    ? `encash ${formatLeaveDays(
                      offboardingCase.leave_settlement.encash_days || 0,
                    )} projected days`
                    : `${formatLeaveDays(
                      offboardingCase.leave_settlement.offset_days || 0,
                    )} days offset · ${formatLeaveDays(
                      offboardingCase.leave_settlement.encash_days || 0,
                    )} days projected for encashment`}
                </p>
              )}
            </div>

            <div className="bg-white rounded-2xl border shadow-sm p-5 space-y-3">
              <h2 className="text-base font-semibold text-gray-900">Your tasks</h2>
              {employeeTasks.length === 0 && (
                <div className="text-center py-12">
                  <CheckCircle2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No employee tasks</h3>
                  <p className="text-sm text-gray-500">HR has not assigned anything for you to complete.</p>
                </div>
              )}
              {employeeTasks.map((task) => {
                const done = ['completed', 'skipped', 'waived'].includes(task.status);
                return (
                  <div key={task.id} className="rounded-xl border border-gray-200 p-4">
                    <div className="flex items-start gap-3 min-w-0">
                      {done ? (
                        <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      ) : (
                        <Circle className="w-5 h-5 text-gray-300 flex-shrink-0 mt-0.5" />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-900">{task.task_name}</p>
                            <p className="text-xs text-gray-500 mt-0.5">{task.description}</p>
                          </div>
                          <span className="text-xs text-gray-400 capitalize flex-shrink-0">
                            {task.status.replace(/_/g, ' ')}
                          </span>
                        </div>

                        {task.task_key === 'flight_readiness_acknowledgement' && !done && (
                          <div className="mt-4 bg-gray-50 rounded-xl p-3">
                            <div className="flex items-start gap-2">
                              <Plane className="w-4 h-4 text-blue-700 flex-shrink-0 mt-0.5" aria-hidden="true" />
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-gray-900">
                                  {flightArrangementsReady
                                    ? `Scheduled departure: ${formatDate(offboardingCase.departure_date)}`
                                    : 'Flight arrangements are being prepared'}
                                </p>
                                <p className="text-xs text-gray-500 mt-0.5">
                                  {flightArrangementsReady
                                    ? `Ticket on file: ${offboardingCase.flight_ticket_filename || 'Confirmed by Operations'}`
                                    : 'You can confirm this task after Operations records the booked flight and ticket.'}
                                </p>
                              </div>
                            </div>
                            {flightArrangementsReady && (
                              <button
                                type="button"
                                className="btn btn-primary btn-sm w-full sm:w-auto mt-3"
                                disabled={completeTask.isPending}
                                onClick={() => setConfirmFlight(true)}
                              >
                                Confirm I understand
                              </button>
                            )}
                          </div>
                        )}

                        {task.task_key === 'flight_readiness_acknowledgement' && done && (
                          <p className="text-xs text-green-700 mt-2">
                            Flight details confirmed
                            {task.payload?.scheduled_departure_date
                              ? ` for ${formatDate(task.payload.scheduled_departure_date)}`
                              : ''}
                          </p>
                        )}

                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={confirmFlight}
        theme="employee"
        variant="primary"
        title="Confirm your flight details"
        confirmLabel="Confirm"
        isLoading={completeTask.isPending}
        onCancel={() => setConfirmFlight(false)}
        onConfirm={completeFlightReadiness}
      >
        <p className="text-sm text-gray-700">
          I understand that my scheduled departure is{' '}
          <span className="font-medium">{formatDate(offboardingCase?.departure_date)}</span>,
          and I am prepared for the return journey.
        </p>
      </ConfirmModal>

    </DashboardLayout>
  );
};

export default SelfServiceOffboarding;
